import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useStoreConfig } from "../features/config/queries";
import { SITE_URL, SITE_NAME as FALLBACK_SITE_NAME, DEFAULT_OG_IMAGE } from "./seoConstants";

function setMetaByName(name: string, content: string): string {
  let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  const previous = el.getAttribute("content") ?? "";
  el.setAttribute("content", content);
  return previous;
}

function setMetaByProperty(property: string, content: string): string {
  let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  const previous = el.getAttribute("content") ?? "";
  el.setAttribute("content", content);
  return previous;
}

// Returns the previous content, or null if the tag didn't exist — pass that
// straight back to restoreMetaByProperty to put things back on unmount.
function removeMetaByProperty(property: string): string | null {
  const el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
  if (!el) return null;
  const previous = el.getAttribute("content");
  el.remove();
  return previous;
}

function restoreMetaByProperty(property: string, previous: string | null) {
  if (previous === null) {
    document.querySelector(`meta[property="${property}"]`)?.remove();
  } else {
    setMetaByProperty(property, previous);
  }
}

function setCanonical(href: string): string {
  let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  const previous = el.getAttribute("href") ?? "";
  el.setAttribute("href", href);
  return previous;
}

export interface DocumentMetaOptions {
  // "product" on PDPs so shares render a product-style OG card; every
  // other page is a plain "website".
  ogType?: "website" | "product";
  // Pages that shouldn't appear in search results (account, cart,
  // checkout). This is on top of, not instead of, the matching robots.txt
  // Disallow rules for those same paths.
  noindex?: boolean;
}

/**
 * Sets the page title, meta description/keywords, canonical URL, and
 * Open Graph/Twitter Card tags for the current route. Canonical/OG URL is
 * derived from the current pathname (query strings stripped, e.g. sort or
 * pagination params on category pages) so those variants aren't indexed as
 * separate pages. Every value is restored on unmount so the previous
 * page's tags don't leak into the next one during client-side navigation.
 */
export function useDocumentMeta(
  title: string,
  description?: string,
  keywords?: string,
  ogImage?: string,
  options: DocumentMetaOptions = {}
) {
  const { ogType = "website", noindex = false } = options;
  const { pathname } = useLocation();
  const { data: config } = useStoreConfig();
  const siteName = config?.storeName || FALLBACK_SITE_NAME;
  const defaultDescription = `${siteName} — considered essentials, made to last.`;
  const finalDescription = description ?? defaultDescription;
  // Homepage passes its full, final title (already containing the site
  // name) — don't append the site name a second time in that case.
  const finalTitle = title === siteName || title.startsWith(siteName) ? title : `${title} — ${siteName}`;
  // Strip any trailing slash on non-root paths so /product/x/ and /product/x
  // don't self-canonicalize as two distinct URLs. The root path keeps its
  // single trailing slash to match index.html's static canonical, the
  // sitemap, and the site-wide JSON-LD, all of which use "SITE_URL/".
  const normalizedPath = pathname === "/" ? "/" : pathname.replace(/\/+$/, "");
  const canonicalUrl = `${SITE_URL}${normalizedPath}`;
  const image = ogImage || DEFAULT_OG_IMAGE;
  const hasCustomImage = !!ogImage;

  useEffect(() => {
    const previousTitle = document.title;
    document.title = finalTitle;

    const prevDescription = setMetaByName("description", finalDescription);
    const prevKeywords = setMetaByName("keywords", keywords ?? "");
    const prevCanonical = setCanonical(canonicalUrl);
    const prevRobots = setMetaByName("robots", noindex ? "noindex, nofollow" : "index, follow");

    const prevOgTitle = setMetaByProperty("og:title", finalTitle);
    const prevOgDescription = setMetaByProperty("og:description", finalDescription);
    const prevOgUrl = setMetaByProperty("og:url", canonicalUrl);
    const prevOgImage = setMetaByProperty("og:image", image);
    const prevOgType = setMetaByProperty("og:type", ogType);
    const prevOgSiteName = setMetaByProperty("og:site_name", siteName);
    // The static width/height in index.html describe the default logo image
    // only — asserting them for a page-specific image (e.g. a real product
    // photo of a different size) would misdescribe it, so drop them rather
    // than claim a wrong size. Only touched (and only restored) when this
    // page actually overrides the image.
    const prevOgImageWidth = hasCustomImage ? removeMetaByProperty("og:image:width") : null;
    const prevOgImageHeight = hasCustomImage ? removeMetaByProperty("og:image:height") : null;

    const prevTwCard = setMetaByName("twitter:card", "summary_large_image");
    const prevTwTitle = setMetaByName("twitter:title", finalTitle);
    const prevTwDescription = setMetaByName("twitter:description", finalDescription);
    const prevTwImage = setMetaByName("twitter:image", image);

    return () => {
      document.title = previousTitle;
      setMetaByName("description", prevDescription);
      setMetaByName("keywords", prevKeywords);
      setCanonical(prevCanonical);
      setMetaByName("robots", prevRobots);
      setMetaByProperty("og:title", prevOgTitle);
      setMetaByProperty("og:description", prevOgDescription);
      setMetaByProperty("og:url", prevOgUrl);
      setMetaByProperty("og:image", prevOgImage);
      setMetaByProperty("og:type", prevOgType);
      setMetaByProperty("og:site_name", prevOgSiteName);
      if (hasCustomImage) {
        restoreMetaByProperty("og:image:width", prevOgImageWidth);
        restoreMetaByProperty("og:image:height", prevOgImageHeight);
      }
      setMetaByName("twitter:card", prevTwCard);
      setMetaByName("twitter:title", prevTwTitle);
      setMetaByName("twitter:description", prevTwDescription);
      setMetaByName("twitter:image", prevTwImage);
    };
  }, [finalTitle, finalDescription, keywords, canonicalUrl, image, siteName, ogType, noindex, hasCustomImage]);
}
